import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Box, CircularProgress, Typography, Paper, Alert } from '@mui/material';
import { Cancel as CancelIcon, Error as ErrorIcon } from '@mui/icons-material';
import { getApiBaseUrl } from '../../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

const DistributionDeny: React.FC = () => {
  const { distributionId } = useParams<{ distributionId: string }>();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleDeny = async () => {
      if (!distributionId) {
        setError('Invalid distribution link');
        setLoading(false);
        return;
      }

      try {
        // Call backend API to deny the distribution request
        const response = await axios.get(
          `${API_BASE_URL}/api/distribution/respond/deny/${distributionId}`,
          {
            responseType: 'text',
            headers: {
              'Accept': 'text/html, application/json'
            }
          }
        );

        setSuccess(true);
      } catch (err: any) {
        console.error('Error denying distribution:', err);
        const errorMessage = err.response?.data || err.message || 'Failed to decline distribution request';
        setError(typeof errorMessage === 'string' ? errorMessage : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    handleDeny();
  }, [distributionId]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
          background: 'linear-gradient(135deg, #09273b 0%, #0927eb 100%)'
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Processing your response...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Paper
          elevation={4}
          sx={{
            maxWidth: 600,
            width: '100%',
            p: 4,
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#d32f2f' }}>
            Error Processing Request
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Please contact support if this issue persists.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: 'linear-gradient(135deg, #09273b 0%, #0927eb 100%)'
      }}
    >
      <Paper
        elevation={8}
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
          background: 'white',
          border: '2px solid #09273b'
        }}
      >
        <CancelIcon sx={{ fontSize: 80, color: '#ff5200', mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#09273b' }}>
          Request Declined
        </Typography>
        <Typography variant="h6" sx={{ mb: 2, color: '#09273b', fontWeight: 600 }}>
          Your response has been recorded.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: '#4A5568', lineHeight: 1.6 }}>
          Thank you for letting us know. We appreciate your time and consideration.
        </Typography>
        <Typography variant="body2" sx={{ color: '#4A5568' }}>
          You can close this window now.
        </Typography>
      </Paper>
    </Box>
  );
};

export default DistributionDeny;

